import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from 'reactstrap';
import { CheckCircle, XCircle, FileText, Edit } from 'lucide-react';
import jsPDF from 'jspdf';
import { AppContext } from '../application/provider';
import * as PapeletaService from '../axios_services/papeletasalida.service';
import * as PermisoService from '../axios_services/permisosalida.service';
import Loader from '../components/Loader/Loader';
import { ButtonInsert, ButtonSearch } from '../components/Buttons/Buttons';
import API_URL_BASE from '../axios_services/apiConfig';
import ModalPapeletaSalida from '../components/Modal/ModalPapeletaSalida';
import ModalRechazarPapeleta from '../components/Modal/ModalRechazarPapeleta';
import ModalAprobarTH from '../components/Modal/ModalAprobarTH';
import ModalPermisoSalida from '../components/Modal/ModalPermisoSalida';
import ModalRechazarPermiso from '../components/Modal/ModalRechazarPermiso';
import ModalAprobarTHPermiso from '../components/Modal/ModalAprobarTHPermiso';
import ModalUpdateTipoSalidaPapeleta from '../components/Modal/ModalUpdateTipoSalidaPapeleta';
import ModalUpdateTipoSalidaPermiso from '../components/Modal/ModalUpdateTipoSalidaPermiso';
import TabsWithTable from '../components/Tabs/TabsWithTable';
import ModalConfirmar from '../components/Modal/ModalConfirmar';
import '../components/Tablas/AccionesProspecto.css';
import './PapeletaSalida.css';

// ── Constantes ────────────────────────────────────────────────────────────────
// Perfil 26 administra las aprobaciones de salidas; se mantiene perfil 5 para cuando vuelva
const ESTADO_LABELS = { 1: 'PENDIENTE JEFE', 2: 'PENDIENTE ADMINISTRACIÓN', 3: 'APROBADO', 4: 'RECHAZADO' };
const MOTIVO_LABELS = {
    PERSONAL: 'PERSONAL',
    MEDICO: 'MÉDICO / SALUD',
    COMISION: 'COMISIÓN DE SERVICIO',
    TRAMITES: 'TRÁMITES PERSONALES',
    CAPACITACION: 'CAPACITACIÓN',
    OTROS: 'OTROS',
};

const formatTime12h = (time24) => {
    if (!time24) return '—';
    const [hrs, mins] = time24.split(':');
    let h = parseInt(hrs, 10);
    const m = mins.substring(0, 2);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${String(h).padStart(2, '0')}:${m} ${ampm}`;
};

// Perfil 26 administra Talento Humano; se mantiene el perfil 5 para cuando vuelva
// const TH_PERFILES = [5, 22]; // Código anterior
const TH_PERFILES = [5, 8, 22, 26];
const JEFE_PERFILES = [1, 2, 3, 5, 6, 9, 26, 30, 32];
const REPORTE_PERFILES = [26, 22, 9, 5, 8];

const now = new Date();
const mesActual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

// ── Estilos por estado ────────────────────────────────────────────────────────
const getRowStyle = (estado) => {
    const e = Number(estado);
    if (e === 1) return { backgroundColor: '#fef3c7' };
    if (e === 2) return { backgroundColor: '#dbeafe' };
    if (e === 3) return { backgroundColor: '#dcfce7' };
    if (e === 4) return { backgroundColor: '#fee2e2' };
    return {};
};

// ── Componente principal ──────────────────────────────────────────────────────
function GestionSalidas() {
    const [state] = useContext(AppContext);

    const storedAuth = JSON.parse(localStorage.getItem('authData') || '{}');
    const userId = Number(state.user?.usuario_id || storedAuth.user?.usuario_id);
    const perfilId = Number(state.user?.perfil_id || storedAuth.user?.perfil_id);
    const esTH = TH_PERFILES.includes(perfilId);
    const esReporte = REPORTE_PERFILES.includes(perfilId);
    const esPermitidoActualizarTipo = perfilId === 26 || perfilId === 8;

    // ── Estado Global ─────────────────────────────────────────────────────────
    const [periodoInput, setPeriodoInput] = useState(mesActual);
    const [periodo, setPeriodo] = useState(mesActual);
    const [loading, setLoading] = useState(false);

    // Papeletas y Permisos raw
    const [papeletas, setPapeletas] = useState([]);
    const [permisos, setPermisos] = useState([]);

    // Estado de Modales
    const [modalCrearPap, setModalCrearPap] = useState(false);
    const [modalCrearPer, setModalCrearPer] = useState(false);
    const [confirmandoPap, setConfirmandoPap] = useState(null);
    const [confirmandoPer, setConfirmandoPer] = useState(null);

    const [modalRechazarPap, setModalRechazarPap] = useState(false);
    const [papeletaRechazar, setPapeletaRechazar] = useState(null);
    const [modalRechazarPer, setModalRechazarPer] = useState(false);
    const [permisoRechazar, setPermisoRechazar] = useState(null);

    const [tipoRechazo, setTipoRechazo] = useState(null);

    const [modalAprobarTHPap, setModalAprobarTHPap] = useState(false);
    const [papeletaAprobarTH, setPapeletaAprobarTH] = useState(null);
    const [modalAprobarTHPer, setModalAprobarTHPer] = useState(false);
    const [permisoAprobarTH, setPermisoAprobarTH] = useState(null);

    const [modalActualizarTipoSalida, setModalActualizarTipoSalida] = useState(false);
    const [papeletaActualizarTipo, setPapeletaActualizarTipo] = useState(null);

    const [modalActualizarTipoSalidaPer, setModalActualizarTipoSalidaPer] = useState(false);
    const [permisoActualizarTipo, setPermisoActualizarTipo] = useState(null);

    const initialFetch = useRef(false);

    // ── Carga inicial ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!initialFetch.current && perfilId) {
            loadAll();
            initialFetch.current = true;
        }
    }, [perfilId]);

    const loadAll = async () => {
        setPeriodo(periodoInput);
        setLoading(true);
        try {
            const [resPap, resPer] = await Promise.all([
                PapeletaService.getPapeletas(),
                PermisoService.getPermisos()
            ]);
            setPapeletas(resPap.data || []);
            setPermisos(resPer.data || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    // ── Filtrado ──────────────────────────────────────────────────────────────
    const filtrarPorMes = (lista, mes) => {
        const filtrada = mes ? lista.filter(p => {
            if (!p.fecha_salida) return false;
            if (p.fecha_salida.startsWith(mes)) return true;
            try {
                const dateObj = new Date(p.fecha_salida);
                const year = dateObj.getFullYear();
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                return `${year}-${month}` === mes;
            } catch (e) {
                return false;
            }
        }) : lista;
        return [...filtrada].sort((a, b) => {
            const ca = a.created_at || a.fecha_salida || '';
            const cb = b.created_at || b.fecha_salida || '';
            return cb.localeCompare(ca);
        });
    };

    const papeletasFiltradas = filtrarPorMes(papeletas, periodo).map(p => ({ ...p, _tipo_doc: 'PAPELETA' }));
    const permisosFiltrados = filtrarPorMes(permisos, periodo).map(p => ({ ...p, _tipo_doc: 'PERMISO' }));

    // Clasificación por tab
    const misPapeletas = papeletasFiltradas.filter(p => Number(p.usuario_id) === userId);
    const misPermisos = permisosFiltrados.filter(p => Number(p.usuario_id) === userId);

    const esJefeDeFila = (p) => Number(p.jefe_usuario_id) === userId || (p.jefe_perfil_id && Number(p.jefe_perfil_id) === perfilId);

    const bandejaJefePap = papeletasFiltradas.filter(esJefeDeFila);
    const bandejaJefePer = permisosFiltrados.filter(esJefeDeFila);
    const bandejaJefeUni = [...bandejaJefePap, ...bandejaJefePer].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    const esJefeBandeja = JEFE_PERFILES.includes(perfilId) || bandejaJefeUni.length > 0;

    const bandejaTHPap = papeletasFiltradas.filter(p => Number(p.estado) >= 2 || (Number(p.estado) === 1 && !p.jefe_perfil_id));
    const bandejaTHPer = permisosFiltrados.filter(p => Number(p.estado) >= 2 || (Number(p.estado) === 1 && !p.jefe_perfil_id));
    const bandejaTHUni = [...bandejaTHPap, ...bandejaTHPer].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

    const reportePap = papeletasFiltradas;
    const reportePer = permisosFiltrados;

    // Badges
    const pendientesJefeUni = bandejaJefeUni.filter(p => Number(p.estado) === 1).length;
    const pendientesTHUni = bandejaTHUni.filter(p => Number(p.estado) === 2 || (Number(p.estado) === 1 && !p.jefe_perfil_id)).length;

    // ── Handlers de PDF ───────────────────────────────────────────────────────
    const loadImg = (url) => new Promise(async (res) => {
        if (!url) return res(null);
        if (typeof url === 'string' && url.startsWith('data:image')) return res(url);
        try {
            let fetchUrl = url;
            if (typeof fetchUrl === 'string' && !fetchUrl.startsWith('http://') && !fetchUrl.startsWith('https://')) {
                const apiHost = API_URL_BASE.replace(/\/api\/?$/, '');
                fetchUrl = `${apiHost}/storage/${fetchUrl.replace(/^storage\//, '').replace(/^\//, '')}`;
            }
            const r = await fetch(fetchUrl);
            if (!r.ok) return res(null);
            const b = await r.blob();
            const rd = new FileReader();
            rd.onloadend = () => res(rd.result);
            rd.onerror = () => res(null);
            rd.readAsDataURL(b);
        } catch { res(null); }
    });

    const handleGenerarPDFPapeleta = async (p) => {
        const doc = new jsPDF('p', 'mm', 'a4');
        const motivo = (MOTIVO_LABELS[p.motivo] || p.motivo || '').toUpperCase();
        const horaReg = p.hora_regreso ? formatTime12h(p.hora_regreso.substring(0, 5)) : '—';
        const tipo = Number(p.es_remunerado) ? 'REMUNERADO' : 'NO REMUNERADO';

        doc.setDrawColor(29, 52, 97); doc.setLineWidth(0.8); doc.line(15, 15, 195, 15);
        try { const l = await loadImg(`${window.location.origin}/logo_mec.png`); if (l) doc.addImage(l, undefined, 15, 18, 18, 18); } catch { }

        doc.setFontSize(16); doc.setTextColor(29, 52, 97); doc.setFont('helvetica', 'bold');
        doc.text('PAPELETA DE SALIDA', 45, 30);
        doc.setFontSize(10); doc.setTextColor(85, 85, 85); doc.setFont('helvetica', 'normal');
        doc.text(`N° ${p.approved_seq || '—'}`, 195, 25, { align: 'right' });
        doc.text(`Fecha registro: ${p.created_at ? p.created_at.substring(0, 10) : ''}`, 195, 30, { align: 'right' });

        doc.setDrawColor(221, 227, 240); doc.setLineWidth(0.2); doc.line(15, 40, 195, 40);

        let y = 50;
        const row = (l1, v1, l2, v2, curY) => {
            const rowH = 12;
            doc.setFillColor(240, 244, 255); doc.rect(15, curY - 5, 35, rowH, 'F');
            doc.setFont('helvetica', 'bold'); doc.text(l1, 17, curY + 1.5);
            doc.setFont('helvetica', 'normal'); doc.text(v1 || '', 52, curY + 1.5, { maxWidth: 50 });
            doc.setFillColor(240, 244, 255); doc.rect(105, curY - 5, 35, rowH, 'F');
            doc.setFont('helvetica', 'bold'); doc.text(l2, 107, curY + 1.5);
            doc.setFont('helvetica', 'normal'); doc.text(v2 || '', 142, curY + 1.5, { maxWidth: 50 });
            doc.rect(15, curY - 5, 180, rowH); doc.line(50, curY - 5, 50, curY - 5 + rowH); doc.line(105, curY - 5, 105, curY - 5 + rowH); doc.line(140, curY - 5, 140, curY - 5 + rowH);
        };

        doc.setFontSize(9); doc.setTextColor(29, 52, 97); doc.setFont('helvetica', 'bold');
        doc.text('DATOS DEL COLABORADOR', 15, y); y += 5; doc.line(15, y, 195, y); y += 8;
        row('Colaborador', (p.nombre_empleado || '').toUpperCase(), 'Cargo', (p.cargo_empleado || '').toUpperCase(), y); y += 20;

        doc.setFont('helvetica', 'bold');
        doc.text('INFORMACIÓN DE LA SALIDA', 15, y); y += 5; doc.line(15, y, 195, y); y += 8;
        row('Fecha Salida', p.fecha_salida || '', 'Tipo', tipo, y); y += 12;
        row('Hora Salida', formatTime12h((p.hora_salida || '').substring(0, 5)), 'Hora Regreso', horaReg, y); y += 12;
        row('Motivo', motivo, 'Aprobado Admin', p.th_fecha_accion ? p.th_fecha_accion.substring(0, 10) : '', y); y += 12;
        if (p.motivo_detalle) {
            doc.setFillColor(240, 244, 255); doc.rect(15, y - 5, 35, 12, 'F');
            doc.setFont('helvetica', 'bold'); doc.text('Detalle', 17, y + 2.5);
            doc.setFont('helvetica', 'normal'); doc.text(p.motivo_detalle, 52, y + 2.5, { maxWidth: 140 });
            doc.rect(15, y - 5, 180, 12); doc.line(50, y - 5, 50, y + 7); y += 12;
        }

        y += 45;
        const firma = async (imgData, label, subLabel, x) => {
            if (imgData) {
                try {
                    const img = await loadImg(imgData);
                    if (img) doc.addImage(img, undefined, x - 20, y - 22, 40, 18);
                } catch { }
            }
            doc.setDrawColor(0); doc.setLineWidth(0.5); doc.line(x - 30, y, x + 30, y);
            doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.text(label || '', x, y + 4, { align: 'center' });
            doc.setFont('helvetica', 'normal'); doc.text(subLabel, x, y + 8, { align: 'center' });
        };

        // Determinar si la papeleta es desde el 01 de Agosto de 2026:
        const fechaRef = p.fecha_salida || (p.created_at ? p.created_at.substring(0, 10) : '') || '';
        const esDesdeAgosto = fechaRef >= '2026-08-01';

        // Determinar si el jefe inmediato es la Sub Gerencia de Administración (perfil_id = 26):
        const jefeEsSubgerencia =
            Number(p.jefe_perfil_id_real || p.jefe_perfil_id) === 26 ||
            (p.subg_usuario_id && Number(p.jefe_usuario_id_real || p.jefe_usuario_id) === Number(p.subg_usuario_id));

        const nombreSubg = (p.nombre_subg || p.nombre_th || '').trim();
        const firmaSubg = p.subg_firma_url || p.th_firma_b64 || p.th_firma_url;

        const nombreTh5 = (p.nombre_th5 || p.nombre_th || '').trim();
        const firmaTh5 = p.th5_firma_url || p.th_firma_b64 || p.th_firma_url;

        const nombreJefe = (p.nombre_jefe || '').trim();
        const firmaJefe = p.jefe_firma_b64 || p.jefe_firma_url;

        if (esDesdeAgosto) {
            if (jefeEsSubgerencia) {
                // Caso: Subgerencia es también el jefe inmediato -> Solo una firma centrada
                await firma(
                    firmaSubg || firmaJefe,
                    (nombreSubg || nombreJefe).toUpperCase(),
                    'SUB GERENCIA DE ADMINISTRACIÓN',
                    105
                );
            } else {
                // Caso: Desde Agosto con jefe y subgerencia distintos -> Izquierda: Jefe Inmediato, Derecha: Sub Gerencia (perfil 26)
                await firma(
                    firmaJefe,
                    nombreJefe.toUpperCase(),
                    'JEFE INMEDIATO',
                    65
                );
                await firma(
                    firmaSubg,
                    (nombreSubg || 'SUB GERENCIA DE ADMINISTRACIÓN').toUpperCase(),
                    'SUB GERENCIA DE ADMINISTRACIÓN',
                    145
                );
            }
        } else {
            // Caso: Anterior a Agosto -> Izquierda: Jefe Inmediato, Derecha: Jefe de Talento Humano (perfil_id = 5)
            await firma(
                firmaJefe,
                nombreJefe.toUpperCase(),
                'JEFE INMEDIATO',
                65
            );
            await firma(
                firmaTh5,
                (nombreTh5 || 'JEFE DE TALENTO HUMANO').toUpperCase(),
                'JEFE DE TALENTO HUMANO',
                145
            );
        }

        try {
            const formData = new FormData(); formData.append('papeleta_id', p.papeleta_id); formData.append('usuario_id', userId);
            formData.append('archivo', new File([doc.output('blob')], `papeleta_${p.papeleta_id}.pdf`, { type: 'application/pdf' }));
            const res = await PapeletaService.guardarArchivoPapeleta(formData);
            window.open(res.data.url, '_blank'); loadAll();
        } catch { window.open(doc.output('bloburl'), '_blank'); }
    };

    const handleGenerarPDFPermiso = async (p) => {
        const doc = new jsPDF('p', 'mm', 'a4');
        const motivo = (MOTIVO_LABELS[p.motivo] || p.motivo || '').toUpperCase();

        doc.setDrawColor(29, 52, 97); doc.setLineWidth(0.8); doc.line(15, 15, 195, 15);
        try { const l = await loadImg(`${window.location.origin}/logo_mec.png`); if (l) doc.addImage(l, undefined, 15, 18, 18, 18); } catch { }

        doc.setFontSize(16); doc.setTextColor(29, 52, 97); doc.setFont('helvetica', 'bold');
        doc.text('PERMISO DE SALIDA', 45, 30);
        doc.setFontSize(10); doc.setTextColor(85, 85, 85); doc.setFont('helvetica', 'normal');
        doc.text(`N° ${p.approved_seq || '—'}`, 195, 25, { align: 'right' });
        doc.text(`Fecha registro: ${p.created_at ? p.created_at.substring(0, 10) : ''}`, 195, 30, { align: 'right' });

        doc.setDrawColor(221, 227, 240); doc.setLineWidth(0.2); doc.line(15, 40, 195, 40);

        let y = 50;
        const row = (l1, v1, l2, v2, curY) => {
            const rowH = 12;
            doc.setFillColor(240, 244, 255); doc.rect(15, curY - 5, 35, rowH, 'F');
            doc.setFont('helvetica', 'bold'); doc.text(l1, 17, curY + 1.5);
            doc.setFont('helvetica', 'normal'); doc.text(v1 || '', 52, curY + 1.5, { maxWidth: 50 });
            doc.setFillColor(240, 244, 255); doc.rect(105, curY - 5, 35, rowH, 'F');
            doc.setFont('helvetica', 'bold'); doc.text(l2, 107, curY + 1.5);
            doc.setFont('helvetica', 'normal'); doc.text(v2 || '', 142, curY + 1.5, { maxWidth: 50 });
            doc.rect(15, curY - 5, 180, rowH); doc.line(50, curY - 5, 50, curY - 5 + rowH); doc.line(105, curY - 5, 105, curY - 5 + rowH); doc.line(140, curY - 5, 140, curY - 5 + rowH);
        };

        doc.setFontSize(9); doc.setTextColor(29, 52, 97); doc.setFont('helvetica', 'bold');
        doc.text('DATOS DEL COLABORADOR', 15, y); y += 5; doc.line(15, y, 195, y); y += 8;
        row('Colaborador', (p.nombre_empleado || '').toUpperCase(), 'Cargo', (p.cargo_empleado || '').toUpperCase(), y); y += 20;

        doc.setFont('helvetica', 'bold');
        doc.text('INFORMACIÓN DEL PERMISO', 15, y); y += 5; doc.line(15, y, 195, y); y += 8;
        row('Fecha Salida', p.fecha_salida || '', 'Fecha Regreso', p.fecha_regreso || '—', y); y += 12;
        row('Motivo', motivo, 'Aprobado Admin', p.th_fecha_accion ? p.th_fecha_accion.substring(0, 10) : '', y); y += 12;
        if (p.motivo_detalle) {
            doc.setFillColor(240, 244, 255); doc.rect(15, y - 5, 35, 12, 'F');
            doc.setFont('helvetica', 'bold'); doc.text('Detalle', 17, y + 2.5);
            doc.setFont('helvetica', 'normal'); doc.text(p.motivo_detalle, 52, y + 2.5, { maxWidth: 140 });
            doc.rect(15, y - 5, 180, 12); doc.line(50, y - 5, 50, y + 7); y += 12;
        }

        y += 45;
        const firmaPer = async (imgData, label, subLabel, x) => {
            if (imgData) {
                try {
                    const img = await loadImg(imgData);
                    if (img) doc.addImage(img, undefined, x - 20, y - 22, 40, 18);
                } catch { }
            }
            doc.setDrawColor(0); doc.setLineWidth(0.5); doc.line(x - 30, y, x + 30, y);
            doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.text(label || '', x, y + 4, { align: 'center' });
            doc.setFont('helvetica', 'normal'); doc.text(subLabel, x, y + 8, { align: 'center' });
        };

        // Determinar si el permiso es desde el 01 de Agosto de 2026:
        const fechaRefPer = p.fecha_salida || (p.created_at ? p.created_at.substring(0, 10) : '') || '';
        const esDesdeAgostoPer = fechaRefPer >= '2026-08-01';

        // Determinar si el jefe inmediato es la Sub Gerencia de Administración (perfil_id = 26):
        const jefeEsSubgerenciaPer =
            Number(p.jefe_perfil_id_real || p.jefe_perfil_id) === 26 ||
            (p.subg_usuario_id && Number(p.jefe_usuario_id_real || p.jefe_usuario_id) === Number(p.subg_usuario_id));

        const nombreSubgPer = (p.nombre_subg || p.nombre_th || '').trim();
        const firmaSubgPer = p.subg_firma_url || p.th_firma_b64 || p.th_firma_url;

        const nombreTh5Per = (p.nombre_th5 || p.nombre_th || '').trim();
        const firmaTh5Per = p.th5_firma_url || p.th_firma_b64 || p.th_firma_url;

        const nombreJefePer = (p.nombre_jefe || '').trim();
        const firmaJefePer = p.jefe_firma_b64 || p.jefe_firma_url;

        if (esDesdeAgostoPer) {
            if (jefeEsSubgerenciaPer) {
                // Caso: Subgerencia es también el jefe inmediato -> Solo una firma centrada
                await firmaPer(
                    firmaSubgPer || firmaJefePer,
                    (nombreSubgPer || nombreJefePer).toUpperCase(),
                    'SUB GERENCIA DE ADMINISTRACIÓN',
                    105
                );
            } else {
                // Caso: Desde Agosto con jefe y subgerencia distintos -> Izquierda: Jefe Inmediato, Derecha: Sub Gerencia (perfil 26)
                await firmaPer(
                    firmaJefePer,
                    nombreJefePer.toUpperCase(),
                    'JEFE INMEDIATO',
                    65
                );
                await firmaPer(
                    firmaSubgPer,
                    (nombreSubgPer || 'SUB GERENCIA DE ADMINISTRACIÓN').toUpperCase(),
                    'SUB GERENCIA DE ADMINISTRACIÓN',
                    145
                );
            }
        } else {
            // Caso: Anterior a Agosto -> Izquierda: Jefe Inmediato, Derecha: Jefe de Talento Humano (perfil_id = 5)
            await firmaPer(
                firmaJefePer,
                nombreJefePer.toUpperCase(),
                'JEFE INMEDIATO',
                65
            );
            await firmaPer(
                firmaTh5Per,
                (nombreTh5Per || 'JEFE DE TALENTO HUMANO').toUpperCase(),
                'JEFE DE TALENTO HUMANO',
                145
            );
        }

        try {
            const formData = new FormData(); formData.append('permiso_id', p.permiso_id); formData.append('usuario_id', userId);
            formData.append('archivo', new File([doc.output('blob')], `permiso_${p.permiso_id}.pdf`, { type: 'application/pdf' }));
            const res = await PermisoService.guardarArchivoPermiso(formData);
            window.open(res.data.url, '_blank'); loadAll();
        } catch { window.open(doc.output('bloburl'), '_blank'); }
    };

    // ── Aprobación Jefe ───────────────────────────────────────────────────────
    const doAprobarJefePap = async () => {
        const { item: p } = confirmandoPap; setConfirmandoPap(null);
        try { await PapeletaService.aprobarJefe({ papeleta_id: p.papeleta_id }); loadAll(); }
        catch (err) { alert(err.response?.data?.error || 'Error al aprobar.'); }
    };

    const doAprobarJefePer = async () => {
        const { item: p } = confirmandoPer; setConfirmandoPer(null);
        try { await PermisoService.aprobarJefe({ permiso_id: p.permiso_id }); loadAll(); }
        catch (err) { alert(err.response?.data?.error || 'Error al aprobar.'); }
    };

    // ── Render Tabla Unificada (Soporta Papeletas y Permisos) ─────────────────
    const renderTabla = (rows, mostrarColab = false, tipoVista = null, unificada = false, tipoEspecifico = null) => {
        const showHora = unificada || tipoEspecifico === 'PAPELETA';
        const showFechaRegreso = unificada || tipoEspecifico === 'PERMISO';

        return (
            <div className="table-container" style={{ overflowX: 'auto', background: 'white' }}>
                <Table hover size="sm" className="mb-0">
                    <thead>
                        <tr>
                            {mostrarColab && <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '180px' }}>COLABORADOR</th>}
                            {mostrarColab && <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '150px' }}>CARGO</th>}
                            {unificada && <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '100px' }}>TIPO</th>}
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '110px' }}>FECHA SALIDA</th>
                            {showFechaRegreso && <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '120px' }}>FECHA REGRESO</th>}
                            {showHora && <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '110px' }}>HORA SALIDA</th>}
                            {showHora && <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '110px' }}>HORA REGRESO</th>}
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '150px' }}>MOTIVO</th>
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '130px' }}>TIPO SALIDA</th>
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '130px' }}>ESTADO</th>
                            <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '200px' }}>DETALLES</th>
                            {tipoVista && <th className="bg-gray-700 text-white uppercase" style={{ minWidth: '120px', textAlign: 'center' }}>ACCIONES</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 0 ? rows.map((p) => {
                            const esPapeleta = p._tipo_doc === 'PAPELETA';
                            const estadoNum = Number(p.estado);
                            const esPendJefe = tipoVista === 'jefe' && estadoNum === 1;
                            const esPendTH = tipoVista === 'th' && (estadoNum === 2 || (estadoNum === 1 && !p.jefe_perfil_id));
                            const mostrarAcc = esPendJefe || esPendTH;

                            return (
                                <tr key={`${p._tipo_doc}_${esPapeleta ? p.papeleta_id : p.permiso_id}`} style={getRowStyle(p.estado)}>
                                    {mostrarColab && <td>{p.nombre_empleado?.toUpperCase()}</td>}
                                    {mostrarColab && <td>{p.cargo_empleado?.toUpperCase()}</td>}
                                    {unificada && <td><span className={`badge ${esPapeleta ? 'bg-primary' : 'bg-info'}`}>{esPapeleta ? 'PAPELETA' : 'PERMISO'}</span></td>}
                                    <td>{p.fecha_salida}</td>
                                    {showFechaRegreso && <td>{p.fecha_regreso || '—'}</td>}
                                    {showHora && <td>{esPapeleta ? formatTime12h(p.hora_salida?.substring(0, 5)) : '—'}</td>}
                                    {showHora && <td>{esPapeleta ? formatTime12h(p.hora_regreso?.substring(0, 5)) : '—'}</td>}
                                    <td>{(MOTIVO_LABELS[p.motivo] || p.motivo || '').toUpperCase()}</td>
                                    <td>{Number(p.es_remunerado) ? 'REMUNERADO' : 'NO REMUNERADO'}</td>
                                    <td>{(ESTADO_LABELS[estadoNum] || '—').toUpperCase()}</td>
                                    <td style={{ minWidth: '200px', fontSize: '11px', color: estadoNum === 4 ? '#991b1b' : '#374151' }}>
                                        {estadoNum === 4 ? (p.observaciones?.toUpperCase() || 'SIN DETALLES') : (p.motivo_detalle?.toUpperCase() || 'SIN DETALLES')}
                                    </td>
                                    {tipoVista && (
                                        <td onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
                                            {mostrarAcc && (
                                                <div className="acciones-container">
                                                    <button className="boton-accion" title="Aprobar"
                                                        onClick={() => {
                                                            if (tipoVista === 'jefe') {
                                                                esPapeleta ? setConfirmandoPap({ item: p }) : setConfirmandoPer({ item: p });
                                                            } else {
                                                                if (esPapeleta) { setPapeletaAprobarTH(p); setModalAprobarTHPap(true); }
                                                                else { setPermisoAprobarTH(p); setModalAprobarTHPer(true); }
                                                            }
                                                        }}>
                                                        <CheckCircle color="#28a745" size={22} />
                                                    </button>
                                                    <button className="boton-accion" title="Rechazar"
                                                        onClick={() => {
                                                            setTipoRechazo(tipoVista === 'jefe' ? 'rechazar_jefe' : 'rechazar_th');
                                                            if (esPapeleta) { setPapeletaRechazar(p); setModalRechazarPap(true); }
                                                            else { setPermisoRechazar(p); setModalRechazarPer(true); }
                                                        }}>
                                                        <XCircle color="#dc3545" size={22} />
                                                    </button>
                                                </div>
                                            )}
                                            {(tipoVista === 'th' || tipoVista === 'reporte' || tipoVista === 'mis_papeletas' || tipoVista === null || tipoVista === 'jefe') && estadoNum === 3 && (
                                                <div className="acciones-container">
                                                    <button className="boton-accion" title={p.pdf_url ? 'Ver PDF' : 'Generar PDF'}
                                                        onClick={() => esPapeleta ? handleGenerarPDFPapeleta(p) : handleGenerarPDFPermiso(p)}>
                                                        <FileText color={p.pdf_url ? '#16a34a' : '#1d4ed8'} size={22} />
                                                    </button>
                                                    {esPapeleta && esPermitidoActualizarTipo && (
                                                        <button className="boton-accion" title="Actualizar Tipo Salida"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setPapeletaActualizarTipo(p);
                                                                setModalActualizarTipoSalida(true);
                                                            }}>
                                                            <Edit color="#f59e0b" size={22} />
                                                        </button>
                                                    )}
                                                    {!esPapeleta && esPermitidoActualizarTipo && (
                                                        <button className="boton-accion" title="Actualizar Tipo Salida"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setPermisoActualizarTipo(p);
                                                                setModalActualizarTipoSalidaPer(true);
                                                            }}>
                                                            <Edit color="#f59e0b" size={22} />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        }) : (
                            <tr><td colSpan={(mostrarColab ? (unificada ? 11 : 10) : 8) + (tipoVista ? 1 : 0)} className="text-center text-gray-500 py-4">No hay registros.</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>
        );
    };

    // ── Array de Tabs Global ──────────────────────────────────────────────────
    const tabs = [
        { key: 'mis_papeletas', label: 'Solicitud de Papeletas', content: renderTabla(misPapeletas, false, null, false, 'PAPELETA') },
        { key: 'mis_permisos', label: 'Solicitud de Permisos', content: renderTabla(misPermisos, false, null, false, 'PERMISO') },
    ];

    if (esJefeBandeja) {
        tabs.push({
            key: 'jefe',
            label: `Bandeja de Aprobación${pendientesJefeUni > 0 ? ` (${pendientesJefeUni})` : ''}`,
            content: renderTabla(bandejaJefeUni, true, 'jefe', true)
        });
    }

    if (esTH) {
        tabs.push({
            key: 'th',
            label: `Bandeja de Entrada${pendientesTHUni > 0 ? ` (${pendientesTHUni})` : ''}`,
            content: renderTabla(bandejaTHUni, true, 'th', true)
        });
    }

    if (esReporte) {
        tabs.push({ key: 'reporte_pap', label: 'Reporte de Papeletas', content: renderTabla(reportePap, true, 'reporte', false, 'PAPELETA') });
        tabs.push({ key: 'reporte_per', label: 'Reporte de Permisos', content: renderTabla(reportePer, true, 'reporte', false, 'PERMISO') });
    }

    const defaultTab = (esTH && pendientesJefeUni === 0) ? 'th' : esJefeBandeja ? 'jefe' : 'mis_papeletas';
    const [tabActiva, setTabActiva] = useState(defaultTab);

    // ── Render Final ──────────────────────────────────────────────────────────
    return (
        <div className="caja-flujo-container">
            {loading && <Loader />}

            <div className="flex items-center justify-between mb-4">
                <h1>GESTIÓN DE SALIDAS</h1>
            </div>

            <div className="filtros" style={{ marginBottom: '10px' }}>
                <div>
                    <label>Mes:</label>
                    <input type="month" value={periodoInput} onChange={e => setPeriodoInput(e.target.value)} />
                </div>
            </div>

            <div className="mb-4">
                <ButtonSearch onClick={loadAll} isLoading={loading} />
            </div>

            {tabActiva === 'mis_papeletas' && (
                <div className="nuevo-movimiento-section mb-3">
                    <ButtonInsert onClick={() => setModalCrearPap(true)} />
                </div>
            )}

            {tabActiva === 'mis_permisos' && (
                <div className="nuevo-movimiento-section mb-3">
                    <ButtonInsert onClick={() => setModalCrearPer(true)} />
                </div>
            )}

            <TabsWithTable tabs={tabs} onTabChange={setTabActiva} defaultTab={defaultTab} />

            {/* ── Modales Papeletas ─────────────────────────────────────────── */}
            <ModalPapeletaSalida
                isOpen={modalCrearPap} onClose={() => setModalCrearPap(false)}
                onRefresh={loadAll} currentUserId={userId}
            />
            <ModalConfirmar
                isOpen={!!confirmandoPap} titulo="Confirmar Aprobación (Jefe)"
                mensaje={`¿Aprobar la papeleta de ${confirmandoPap?.item?.nombre_empleado || 'este colaborador'}?`}
                textConfirmar="Aprobar" textCancelar="Cancelar" variante="exito"
                onConfirmar={doAprobarJefePap} onCancelar={() => setConfirmandoPap(null)}
            />
            <ModalAprobarTH
                isOpen={modalAprobarTHPap} onClose={() => { setModalAprobarTHPap(false); setPapeletaAprobarTH(null); }}
                papeleta={papeletaAprobarTH} onRefresh={loadAll}
            />
            <ModalRechazarPapeleta
                isOpen={modalRechazarPap} onClose={() => { setModalRechazarPap(false); setPapeletaRechazar(null); setTipoRechazo(null); }}
                papeleta={papeletaRechazar} tipo={tipoRechazo} onRefresh={loadAll}
            />
            <ModalUpdateTipoSalidaPapeleta
                isOpen={modalActualizarTipoSalida} onClose={() => { setModalActualizarTipoSalida(false); setPapeletaActualizarTipo(null); }}
                papeleta={papeletaActualizarTipo} onRefresh={loadAll}
            />

            {/* ── Modales Permisos ──────────────────────────────────────────── */}
            <ModalPermisoSalida
                isOpen={modalCrearPer} onClose={() => setModalCrearPer(false)}
                onRefresh={loadAll} currentUserId={userId}
            />
            <ModalConfirmar
                isOpen={!!confirmandoPer} titulo="Confirmar Aprobación (Jefe)"
                mensaje={`¿Aprobar el permiso de ${confirmandoPer?.item?.nombre_empleado || 'este colaborador'}?`}
                textConfirmar="Aprobar" textCancelar="Cancelar" variante="exito"
                onConfirmar={doAprobarJefePer} onCancelar={() => setConfirmandoPer(null)}
            />
            <ModalAprobarTHPermiso
                isOpen={modalAprobarTHPer} onClose={() => { setModalAprobarTHPer(false); setPermisoAprobarTH(null); }}
                permiso={permisoAprobarTH} onRefresh={loadAll}
            />
            <ModalRechazarPermiso
                isOpen={modalRechazarPer} onClose={() => { setModalRechazarPer(false); setPermisoRechazar(null); setTipoRechazo(null); }}
                permiso={permisoRechazar} tipo={tipoRechazo} onRefresh={loadAll}
            />
            <ModalUpdateTipoSalidaPermiso
                isOpen={modalActualizarTipoSalidaPer} onClose={() => { setModalActualizarTipoSalidaPer(false); setPermisoActualizarTipo(null); }}
                permiso={permisoActualizarTipo} onRefresh={loadAll}
            />
        </div>
    );
}

export default GestionSalidas;
